import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { resumeStorage } from '../../services/resumeStorage';
import { database } from '../../services/firebase';
import { ref as dbRef, update, get } from 'firebase/database';
import { FaCloudUploadAlt, FaSpinner } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';
import { analyzeResume } from '../../services/geminiService';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`;

const JobApplication = ({ job, onClose, userId, onSubmit, isSubmitting }) => {
  const [resume, setResume] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setResume(file);
      setError('');
    } else {
      setError('Please upload a PDF file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume) {
      setError('Please upload your resume');
      return;
    }

    setUploading(true);
    try {
      // Upload resume to Firebase Storage
      const storageRef = ref(resumeStorage, `resumes/${userId}_${Date.now()}.pdf`);
      const uploadResult = await uploadBytes(storageRef, resume);
      const resumeUrl = await getDownloadURL(uploadResult.ref);

      // Extract text from PDF for ATS analysis
      const fileReader = new FileReader();
      const pdfData = await new Promise((resolve) => {
        fileReader.onload = () => resolve(fileReader.result);
        fileReader.readAsArrayBuffer(resume);
      });

      const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
      let resumeText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        resumeText += textContent.items.map(item => item.str).join(' ');
      }

      // Analyze resume with ATS
      const analysis = await analyzeResume(resumeText, job.requirements);
      setAnalysisResult(analysis);
      
      if (!analysis.canApply) {
        setError(`Cannot submit application: ${analysis.explanation}`);
        return;
      }

      // Update application object with detailed scores
      const application = {
        jobId: job.id,
        userId,
        status: 'pending',
        appliedAt: new Date().toISOString(),
        resumeUrl,
        atsScores: {
          total: analysis.totalScore,
          skills: analysis.skillsMatch.score,
          experience: analysis.experienceMatch.score,
          certifications: analysis.certificationsMatch.score
        },
        atsAnalysis: {
          matchingSkills: analysis.skillsMatch.matchingSkills,
          missingSkills: analysis.skillsMatch.missingSkills,
          relevantExperience: analysis.experienceMatch.relevantExperience,
          recommendedCertifications: analysis.certificationsMatch.recommendedCertifications
        },
        atsExplanation: {
          strengths: analysis.atsExplanation?.strengths || [],
          weaknesses: analysis.atsExplanation?.weaknesses || [],
          recommendations: analysis.atsExplanation?.recommendations || []
        },
        atsScoresDetailed: {
          ...analysis.atsScores,
          skillsBreakdown: analysis.skillsMatch.score,
          experienceBreakdown: analysis.experienceMatch.score,
          certificationsBreakdown: analysis.certificationsMatch.score
        },
        explanation: analysis.explanation,
        resumeText
      };

      // Update applications in database
      const userRef = dbRef(database, `users/${userId}/applications`);
      const snapshot = await get(userRef);
      const existingApplications = snapshot.exists() ? snapshot.val() : [];
      
      await update(dbRef(database, `users/${userId}`), {
        applications: [...existingApplications, application]
      });

      onClose();
    } catch (error) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Apply for {job.position}</h2>
        <p className="text-gray-600 mb-6">{job.company}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Upload Resume (PDF)
            </label>
            <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-indigo-500 transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="text-center">
                <FaCloudUploadAlt className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  {resume ? resume.name : 'Click or drag to upload PDF'}
                </p>
              </div>
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !resume}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                       disabled:bg-indigo-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {uploading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
        {analysisResult && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-lg mb-2">Resume Analysis</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Skills Match:</span>
                <span className="font-medium text-indigo-600">
                  {analysisResult?.skillsMatch?.score || 0}% of 50%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Experience Match:</span>
                <span className="font-medium text-indigo-600">
                  {analysisResult?.experienceMatch?.score || 0}% of 30%
                </span>
              </div>
              <div className="flex justify-between">
                <span>Certifications Match:</span>
                <span className="font-medium text-indigo-600">
                  {analysisResult?.certificationsMatch?.score || 0}% of 20%
                </span>
              </div>
              <div className="mt-2 pt-2 border-t">
                <div className="flex justify-between">
                  <span className="font-semibold">Total Score:</span>
                  <span className="font-semibold text-indigo-600">
                    {analysisResult?.totalScore || 0}%
                  </span>
                </div>
              </div>
              {analysisResult?.skillsMatch?.missingSkills && analysisResult.skillsMatch.missingSkills.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Missing Skills:</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 mt-1">
                    {analysisResult.skillsMatch.missingSkills.map((skill, index) => (
                      <li key={index}>{skill}</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysisResult?.skillsMatch?.matchingSkills && analysisResult.skillsMatch.matchingSkills.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700">Matching Skills:</p>
                  <ul className="text-sm text-gray-600 list-disc pl-4 mt-1">
                    {analysisResult.skillsMatch.matchingSkills.map((skill, index) => (
                      <li key={index}>{skill}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplication;